<template>
    <div>
        <DataTable
            :data="sortedOrgs || []"
            :columns="organizationColumns"
            :loading="isLoading"
            enable-sorting
            enable-search
            enable-pagination
            search-placeholder="Search organizations"
            table-class="table w-full"
            :grid-mode="displayMode === DisplayMode.GRID"
            @row-click="routeToDetails"
        >
            <template #custom-grid-mode="{ data, handleRowClick }">
                <GridList :sorted-orgs="data" @choose="handleRowClick" />
            </template>

            <template #table-header-actions>
                <div class="gap-md flex">
                    <div class="gap-sm flex justify-center">
                        <button class="btn-link" @click="swapDisplayMode(DisplayMode.LIST)">
                            <div class="flex"><i class="i-menu"></i></div>
                        </button>
                        <button class="btn-link" @click="swapDisplayMode(DisplayMode.GRID)">
                            <div class="flex"><i class="i-grid"></i></div>
                        </button>
                    </div>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script setup lang="ts">
import { ref, type PropType } from 'vue';
import router from '@/router/admin-router';
import DataTable from '@/components/tables/DataTable.vue';
import {
    createCountColumn,
    createServiceLevelColumn,
    createOrganizationNameColumn,
} from '@/components/tables/columns/factories';
import type { AdminOrganizationData } from '@/stores/store-interfaces';
import type { CellContext } from '@tanstack/vue-table';
import GridList from './grid-list.vue';

defineProps({
    isLoading: Boolean,
    sortedOrgs: Array as PropType<AdminOrganizationData[]>,
    sortColumn: String,
    sortDirection: Boolean,
    gridMode: Boolean,
});

enum DisplayMode {
    LIST = 'list',
    GRID = 'grid',
}

const displayMode = ref<DisplayMode>(DisplayMode.LIST);
const swapDisplayMode = (value: DisplayMode) => {
    if (displayMode.value !== value) {
        displayMode.value = value;
    }
};

// Column definitions
const organizationColumns = [
    createOrganizationNameColumn<AdminOrganizationData>('name', 'Name'),
    createCountColumn<AdminOrganizationData>('users_count', 'Users'),
    createCountColumn<AdminOrganizationData>('devices_count', 'Devices'),
    createServiceLevelColumn<AdminOrganizationData>('service_level_id', 'Tiers'),
    {
        accessorKey: 'organization_type.name',
        header: 'Type',
        cell: (info: CellContext<AdminOrganizationData, AdminOrganizationData['organization_type']['name']>) => {
            const value = info.getValue();
            if (!value) return '-';
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
        enableSorting: true,
    },
];

function routeToDetails(org: AdminOrganizationData) {
    router.push({ name: 'org-detail', params: { id: org.organization_id } });
}
</script>
